import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { useMemo } from 'react'
import { toast } from 'sonner'
import { EntityForm } from '@/components/custom/entity-form'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { errorMessage } from '@/lib/error-message'
import { buildExternalFetcher } from '@/lib/external-fetcher'
import { adaptManifestEntity, findEntity } from '@/lib/external-manifest-adapter'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/m/external/$connectionId/$entityKey/$recordId/edit')({
  component: ExternalEditRecordPage,
})

function ExternalEditRecordPage() {
  const { connectionId, entityKey, recordId } = Route.useParams()
  const t = useTranslations()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const fetcher = useMemo(() => buildExternalFetcher(connectionId), [connectionId])

  const conn = useQuery(orpc.external.connections.get.queryOptions({ input: { id: connectionId } }))

  const record = useQuery({
    queryKey: ['external', connectionId, entityKey, 'record', recordId],
    queryFn: () => fetcher.get(entityKey, recordId),
    enabled: conn.isSuccess,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => fetcher.update(entityKey, recordId, data),
    onSuccess: () => {
      toast.success(t('externalRenderer.updated'))
      void queryClient.invalidateQueries({
        queryKey: ['external', connectionId, entityKey],
      })
      void navigate({
        to: '/m/external/$connectionId/$entityKey/$recordId',
        params: { connectionId, entityKey, recordId },
      })
    },
    onError: (err: Error) => toast.error(errorMessage(err, t)),
  })

  if (conn.isLoading || record.isLoading) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-48 mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  const entityDef = findEntity(conn.data?.manifestCachedJson, entityKey)
  if (!entityDef) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold">{t('externalRenderer.unknownEntity')}</h1>
      </div>
    )
  }

  const adapted = adaptManifestEntity(entityDef)
  const initialData = (record.data ?? {}) as Record<string, unknown>

  const candidate = adapted.fields.find(
    (f) => ['text', 'email'].includes(f.type) && initialData[f.key],
  )
  const title = candidate
    ? String(initialData[candidate.key])
    : `${adapted.name} ${recordId.slice(0, 8)}`

  const serviceLabel = conn.data?.displayName ?? 'Service'

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        breadcrumbs={[
          {
            label: serviceLabel,
            to: '/m/external/$connectionId',
            params: { connectionId },
          },
          {
            label: adapted.namePlural,
            to: '/m/external/$connectionId/$entityKey',
            params: { connectionId, entityKey },
          },
          {
            label: title,
            to: '/m/external/$connectionId/$entityKey/$recordId',
            params: { connectionId, entityKey, recordId },
          },
          { label: t('entityRuntime.actions.edit') },
        ]}
        title={`${t('entityRuntime.actions.edit')}: ${title}`}
      />

      <Card>
        <CardContent className="pt-6">
          <EntityForm
            fields={adapted.fields}
            initialData={initialData}
            submitting={updateMutation.isPending}
            submitLabel={t('common.save')}
            onCancel={() =>
              navigate({
                to: '/m/external/$connectionId/$entityKey/$recordId',
                params: { connectionId, entityKey, recordId },
              })
            }
            onSubmit={async (data) => {
              await updateMutation.mutateAsync(data)
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
}
